import { useEffect, useMemo, useState } from "react";
import AppSelect from "../ui/AppSelect";
import debounce from "lodash.debounce";
import position from "../../assets/position.svg";
import telephone from "../../assets/telephone.svg";
import { useAuth } from "../../context/AuthContext";
import { apiClient } from "../../apiClient";
import { LeadCard, PanelFilterBar, PanelHero } from "../ui/panel";

interface Lead {
  id: number;
  nome: string;
  cognome?: string | null;
  settore?: string;
  indirizzo?: string;
  linkedin_url?: string | null;
  azienda?: string | null;
  associato?: string | null;
  servizio_proposto?: string | null;
  esito_contatto?: string | null;
  data_ultimo_contatto?: string | null;
  numero_contatti?: number;
  tipo: "contatto" | "small-lead" | "startup";
}

interface OptionType {
  value: string;
  label: string;
}

export default function DaRicontattare() {
  const { user, isAuthenticated } = useAuth();
  const [leadsDaContattare, setLeadsDaContattare] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTipoLead, setSelectedTipoLead] = useState<OptionType | null>(null);
  const [nomeInput, setNomeInput] = useState("");
  const [nomeFiltro, setNomeFiltro] = useState("");
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const PAGE_SIZE = 25;

  const tipoLeadOptions: OptionType[] = [
    { value: "", label: "Tutti" },
    { value: "contatto", label: "Linkedin" },
    { value: "small-lead", label: "Small Leads" },
    { value: "startup", label: "Start-up" },
  ];

  const fetchDaContattare = async (
    append = false,
    tipoValue = selectedTipoLead?.value || "",
    nomeValue = nomeFiltro
  ) => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = {
        limit: PAGE_SIZE,
        offset: append ? page * PAGE_SIZE : 0,
      };
      if (tipoValue) params.tipo = tipoValue;
      if (nomeValue) params.nome = nomeValue;

      const res = await apiClient.get("contatti/da_contattare", { params });
      const nuovi = res.data.contatti || [];
      setLeadsDaContattare((prev) => (append ? [...prev, ...nuovi] : nuovi));
      setHasMore(nuovi.length === PAGE_SIZE);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const prendiInCarico = async (lead: Lead) => {
    if (!user?.username) return;
    try {
      await apiClient.post(
        `contatti/update_associato?tipo=${lead.tipo}`,
        {
          contatto_id: lead.id,
          nuovo_associato: user.username,
          recycle_snapshot: {
            data_ultimo_contatto: lead.data_ultimo_contatto || null,
            servizio_proposto: lead.servizio_proposto || null,
            esito_contatto: lead.esito_contatto || null,
          },
        }
      );
      setLeadsDaContattare((prev) => prev.filter((item) => !(item.id === lead.id && item.tipo === lead.tipo)));
    } catch (err) {
      console.error("Errore assegnazione lead:", err);
    }
  };

  useEffect(() => {
    setPage(0);
    setHasMore(true);
    fetchDaContattare(false, selectedTipoLead?.value || "", nomeFiltro);
  }, [selectedTipoLead, nomeFiltro, isAuthenticated]);

  useEffect(() => {
    if (page > 0) {
      fetchDaContattare(true, selectedTipoLead?.value || "", nomeFiltro);
    }
  }, [page]);

  const debouncedSetNome = debounce((value: string) => {
    setNomeFiltro(value);
  }, 350);

  useEffect(() => {
    return () => debouncedSetNome.cancel();
  }, [debouncedSetNome]);

  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + window.scrollY >= document.body.offsetHeight - 280 &&
        !loading &&
        hasMore
      ) {
        setPage((prev) => prev + 1);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [loading, hasMore]);

  const tipoBadgeClasses = useMemo(
    () => ({
      contatto: "bg-blue-100 text-blue-700 border-blue-200",
      "small-lead": "bg-emerald-100 text-emerald-700 border-emerald-200",
      startup: "bg-purple-100 text-purple-700 border-purple-200",
    }),
    []
  );

  const tipoLabel = useMemo(
    () => ({
      contatto: "LinkedIn",
      "small-lead": "Small Lead",
      startup: "Start-up",
    }),
    []
  );

  const formatUltimoContatto = (value?: string | null) => {
    if (!value) return "Mai";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString("it-IT");
  };

  return (
    <div className="space-y-6 pb-10">
      <PanelHero
        variant="brand"
        eyebrow="Pipeline"
        title="Da contattare"
        description="Lead da ricontattare o mai contattati. Il contatore riparte da zero al «Prendi in carico»."
      />

      <PanelFilterBar>
        <input
          type="text"
          placeholder="Cerca per nome, cognome o azienda..."
          value={nomeInput}
          onChange={(e) => {
            const value = e.target.value.trimStart();
            setNomeInput(value);
            debouncedSetNome(value);
          }}
          className="min-w-[220px] flex-1 rounded-xl border border-secondary/15 bg-secondary/[0.02] px-3 py-2.5 text-sm outline-none focus:border-primary/40 focus:ring-2 focus:ring-primary/15"
        />
        <div className="w-full sm:w-56">
          <AppSelect
            options={tipoLeadOptions}
            value={selectedTipoLead}
            onChange={setSelectedTipoLead}
            isClearable
            placeholder="Tipo lead"
            isSearchable={false}
            className="w-full"
          />
        </div>
      </PanelFilterBar>

      {leadsDaContattare.length === 0 && !loading && (
        <div className="rounded-2xl border-2 border-dashed border-secondary/15 bg-white py-16 text-center">
          <p className="font-extrabold text-secondary">Nessun contatto in coda</p>
          <p className="mt-1 text-sm text-secondary/50">Prova a cambiare i filtri di ricerca</p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
        {leadsDaContattare.map((lead) => (
          <LeadCard key={`${lead.tipo}-${lead.id}`} className="min-h-[230px]">
            <div className="space-y-3">
              <div className="flex items-start justify-between gap-2">
                <span
                  className={`rounded-full border px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${tipoBadgeClasses[lead.tipo]}`}
                >
                  {tipoLabel[lead.tipo]}
                </span>
                <span className="rounded-md bg-gray-100 px-2 py-1 text-[11px] font-semibold text-gray-600">
                  Contatti: 0
                </span>
              </div>
              <h3 className="text-lg font-bold text-secondary">
                {lead.tipo === "startup" ? (lead.azienda || "Azienda non disponibile") : (lead.azienda || `${lead.nome} ${lead.cognome || ""}`.trim())}
              </h3>
              {lead.tipo === "startup" && !!lead.nome && (
                <p className="text-xs text-gray-400">Referente: {lead.nome} {lead.cognome || ""}</p>
              )}
              {lead.settore && <p className="text-sm text-secondary/70">{lead.settore}</p>}
              <div className="space-y-2 text-sm text-gray-500">
                {lead.indirizzo && (
                  <p className="flex items-center gap-2">
                    <img src={position} alt="Indirizzo" className="h-4 w-4" />
                    <span className="line-clamp-1">{lead.indirizzo}</span>
                  </p>
                )}
                <p className="flex items-center gap-2">
                  <img src={telephone} alt="Ultimo contatto" className="h-4 w-4" />
                  Ultimo contatto: {formatUltimoContatto(lead.data_ultimo_contatto)}
                </p>
              </div>
            </div>
            <div className="mt-5 border-t border-gray-100 pt-4">
              <button
                type="button"
                onClick={() => prendiInCarico(lead)}
                className="w-full cursor-pointer rounded-xl bg-gradient-to-r from-[#cd212a] to-[#a01820] px-3 py-2.5 text-sm font-extrabold text-white shadow-md transition hover:shadow-lg"
              >
                Prendi in carico
              </button>
            </div>
          </LeadCard>
        ))}
      </div>

      {loading && (
        <div className="py-8 text-center text-sm font-medium text-gray-400">
          Caricamento contatti...
        </div>
      )}
      {!loading && leadsDaContattare.length > 0 && !hasMore && (
        <div className="py-6 text-center text-xs font-semibold uppercase tracking-wide text-gray-400">
          Hai visualizzato tutti i contatti da contattare
        </div>
      )}
    </div>
  );
}
